import { format, isPast, differenceInDays } from 'date-fns';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { InventoryItem } from '@/types/inventory';
import { supabase } from "@/integrations/supabase/client";

// Chart colors configuration
export const CHART_COLORS = {
  healthy: "#2ecc71",
  expiringSoon: "#f39c12",
  expired: "#e74c3c",
  chartBackground: "#f8fafc",
  chartBorder: "#e2e8f0",
  barColors: ["#8b5cf6", "#3b82f6", "#10b981", "#f97316", "#ef4444"]
};

export const getItemStatus = (item: InventoryItem) => {
  if (!item.has_expiry || !item.expiry_date) return 'No Expiry';
  
  const today = new Date();
  const expiry = new Date(item.expiry_date);
  
  if (isPast(expiry)) {
    return 'Expired';
  }
  
  const daysUntilExpiry = differenceInDays(expiry, today);
  
  if (daysUntilExpiry <= 7) {
    return 'Expiring Soon';
  }
  
  return 'Good';
};

export const calculateStats = (items: InventoryItem[]) => {
  const today = new Date();
  
  const totalItems = items.length;
  
  const expiredItems = items.filter(item => 
    item.has_expiry && 
    item.expiry_date && 
    isPast(new Date(item.expiry_date))
  ).length;
  
  const expiringSoonItems = items.filter(item => 
    item.has_expiry && 
    item.expiry_date && 
    !isPast(new Date(item.expiry_date)) && 
    differenceInDays(new Date(item.expiry_date), today) <= 7
  ).length;
  
  const healthyItems = totalItems - expiredItems - expiringSoonItems;
  
  let totalShelfLife = 0;
  let itemsWithShelfLife = 0;
  
  items.forEach(item => {
    if (item.has_expiry && item.expiry_date) {
      const expiryDate = new Date(item.expiry_date);
      const createdDate = new Date(item.created_at);
      const shelfLife = differenceInDays(expiryDate, createdDate);
      
      if (shelfLife > 0) {
        totalShelfLife += shelfLife;
        itemsWithShelfLife++;
      }
    }
  });
  
  const averageShelfLife = itemsWithShelfLife > 0 
    ? Math.round(totalShelfLife / itemsWithShelfLife) 
    : 0;

  const categoryMap = new Map<string, number>();
  items.forEach(item => {
    const category = item.category;
    categoryMap.set(category, (categoryMap.get(category) || 0) + 1);
  });
  
  const categoryData = Array.from(categoryMap.entries())
    .map(([name, value], index) => ({
      name,
      value,
      fill: CHART_COLORS.barColors[index % CHART_COLORS.barColors.length]
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 5);
  
  const trendData = [];
  const currentDate = new Date();
  
  for (let i = 0; i < 4; i++) {
    const weekStart = differenceInDays(currentDate, (i + 1) * 7);
    const weekEnd = i === 0 ? currentDate : differenceInDays(currentDate, i * 7);
    const weekLabel = `Week ${4-i}`;
    
    const expiringItems = items.filter(item => {
      if (!item.expiry_date) return false;
      const expiryDate = new Date(item.expiry_date);
      return expiryDate >= new Date(weekStart) && expiryDate <= new Date(weekEnd);
    }).length;
    
    trendData.unshift({
      name: weekLabel,
      value: expiringItems,
      date: format(new Date(weekStart), 'MMM d')
    });
  }
  
  return {
    totalItems,
    expiredItems,
    expiringSoonItems,
    healthyItems,
    averageShelfLife,
    categoryData,
    trendData
  };
};

export const generateFallbackSummary = (stats: ReturnType<typeof calculateStats>) => {
  const { totalItems, expiredItems, expiringSoonItems, healthyItems, averageShelfLife } = stats;
  
  if (totalItems === 0) {
    return "No inventory items found for the selected period. Consider adding items to your inventory to track their expiry dates and manage them effectively.";
  }
  
  const healthyPercent = Math.round((healthyItems / totalItems) * 100);
  const expiredPercent = Math.round((expiredItems / totalItems) * 100);
  const expiringSoonPercent = Math.round((expiringSoonItems / totalItems) * 100);
  
  let summary = `Your inventory contains ${totalItems} items with ${healthyPercent}% in good condition, ${expiringSoonPercent}% expiring soon, and ${expiredPercent}% expired. `;
  
  if (expiredItems > 0) {
    summary += `You should consider removing the ${expiredItems} expired items to maintain inventory hygiene. `;
  }
  
  if (expiringSoonItems > 0) {
    summary += `Pay attention to the ${expiringSoonItems} items expiring soon to reduce potential waste. `;
  }
  
  if (averageShelfLife > 0) {
    summary += `The average shelf life of your inventory is ${averageShelfLife} days. `;
    
    if (averageShelfLife < 14) {
      summary += "This is relatively short, which may indicate you're stocking perishable goods. Consider reviewing your purchasing frequency to optimize freshness.";
    } else if (averageShelfLife < 30) {
      summary += "This indicates a moderate shelf life for your inventory. Your current purchasing strategy appears balanced.";
    } else {
      summary += "Your inventory consists of items with good longevity, which allows for less frequent inventory replenishment.";
    }
  }
  
  return summary;
};

export const generatePdfReport = async (
  filteredItems: InventoryItem[], 
  stats: ReturnType<typeof calculateStats>,
  aiSummary: string | null,
  user: any,
  fromDate: Date | undefined,
  toDate: Date | undefined
) => {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });
  
  doc.setProperties({
    title: 'ExpiTrack - Inventory Report',
    author: user?.email || 'ExpiTrack User',
    creator: 'ExpiTrack'
  });
  
  doc.setFillColor(5, 150, 105);
  doc.rect(0, 0, 210, 20, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.text('ExpiTrack - Inventory Report', 105, 12, { align: 'center' });
  
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(10);
  doc.text(`User: ${user?.email || 'Anonymous'}`, 15, 30);
  doc.text(`Date: ${format(new Date(), 'PPP')}`, 15, 35);
  doc.text(`Time: ${format(new Date(), 'pp')}`, 15, 40);
  
  doc.setFontSize(12);
  doc.text('Report Period:', 15, 50);
  doc.setFontSize(10);
  doc.text(`From: ${format(fromDate || new Date(), 'PPP')}`, 25, 55);
  doc.text(`To: ${format(toDate || new Date(), 'PPP')}`, 25, 60);
  
  doc.setFontSize(14);
  doc.text('Inventory Summary', 15, 70);
  
  const summaryBoxes = [
    { label: 'Total Items', value: stats.totalItems, color: [200, 230, 201] },
    { label: 'Expired Items', value: stats.expiredItems, color: [255, 205, 210] },
    { label: 'Expiring Soon', value: stats.expiringSoonItems, color: [255, 224, 178] },
    { label: 'Healthy Items', value: stats.healthyItems, color: [179, 229, 252] },
    { label: 'Avg. Shelf Life', value: `${stats.averageShelfLife} days`, color: [225, 190, 231] }
  ];
  
  let boxX = 15;
  const boxWidth = 34;
  const boxGap = 4;
  
  summaryBoxes.forEach((box) => {
    doc.setFillColor(box.color[0], box.color[1], box.color[2]);
    doc.roundedRect(boxX, 75, boxWidth, 20, 2, 2, 'F');
    
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(8);
    doc.text(box.label, boxX + boxWidth/2, 80, { align: 'center' });
    doc.setFontSize(12);
    doc.text(box.value.toString(), boxX + boxWidth/2, 88, { align: 'center' });
    
    boxX += boxWidth + boxGap;
  });
  
  let currentY = 105;
  
  if (stats.totalItems > 0) {
    const pieRadius = 15;
    const centerX = 40;
    const centerY = currentY + pieRadius;
    
    doc.setFillColor(46, 204, 113);
    doc.circle(centerX - 5, centerY - 5, 5, 'F');
    
    doc.setFillColor(243, 156, 18);
    doc.circle(centerX + 5, centerY - 5, 5, 'F');
    
    doc.setFillColor(231, 76, 60);
    doc.circle(centerX, centerY + 5, 5, 'F');
    
    const healthyPercent = Math.round((stats.healthyItems / stats.totalItems) * 100);
    const expiringSoonPercent = Math.round((stats.expiringSoonItems / stats.totalItems) * 100);
    const expiredPercent = Math.round((stats.expiredItems / stats.totalItems) * 100);
    
    doc.text(`Healthy (${healthyPercent}%)`, 80, currentY + 5);
    doc.setFillColor(46, 204, 113);
    doc.rect(75, currentY + 2, 3, 3, 'F');
    
    doc.text(`Expiring Soon (${expiringSoonPercent}%)`, 80, currentY + 12);
    doc.setFillColor(243, 156, 18);
    doc.rect(75, currentY + 9, 3, 3, 'F');
    
    doc.text(`Expired (${expiredPercent}%)`, 80, currentY + 19);
    doc.setFillColor(231, 76, 60);
    doc.rect(75, currentY + 16, 3, 3, 'F');
  }
  
  currentY += 35;
  
  if (stats.categoryData.length > 0) {
    doc.setFontSize(14);
    doc.text('Top Categories', 15, currentY);
    currentY += 10;
    
    const barHeight = 7;
    const barGap = 3;
    const barMaxWidth = 100;
    
    const maxValue = Math.max(...stats.categoryData.map(item => item.value));
    
    stats.categoryData.forEach((category, index) => {
      const barWidth = (category.value / maxValue) * barMaxWidth;
      const barY = currentY + (index * (barHeight + barGap));
      
      const colorIndex = index % CHART_COLORS.barColors.length;
      const colorHex = CHART_COLORS.barColors[colorIndex];
      
      const r = parseInt(colorHex.slice(1, 3), 16);
      const g = parseInt(colorHex.slice(3, 5), 16);
      const b = parseInt(colorHex.slice(5, 7), 16);
      
      doc.setFillColor(r, g, b);
      doc.rect(40, barY, barWidth, barHeight, 'F');
      
      doc.setFontSize(8);
      doc.text(category.name, 35, barY + barHeight/2 + 1, { align: 'right' });
      
      doc.text(category.value.toString(), 40 + barWidth + 3, barY + barHeight/2 + 1);
    });
    
    currentY += (stats.categoryData.length * (barHeight + barGap)) + 10;
  }
  
  if (stats.trendData.length > 0) {
    doc.setFontSize(14);
    doc.text('Expiry Trend', 15, currentY);
    currentY += 10;
    
    const chartWidth = 120;
    const chartHeight = 40;
    const chartX = 40;
    const chartY = currentY;
    
    doc.setDrawColor(200, 200, 200);
    doc.line(chartX, chartY, chartX, chartY + chartHeight);
    doc.line(chartX, chartY + chartHeight, chartX + chartWidth, chartY + chartHeight);
    
    const maxValue = Math.max(...stats.trendData.map(point => point.value)) || 1;
    const points = stats.trendData.map((point, index) => {
      const x = chartX + (index * (chartWidth / (stats.trendData.length - 1)));
      const y = chartY + chartHeight - ((point.value / maxValue) * chartHeight);
      return { x, y, value: point.value, name: point.name };
    });
    
    doc.setDrawColor(139, 92, 246);
    doc.setLineWidth(1);
    
    for (let i = 0; i < points.length - 1; i++) {
      doc.line(points[i].x, points[i].y, points[i+1].x, points[i+1].y);
    }
    
    points.forEach(point => {
      doc.setFillColor(139, 92, 246);
      doc.circle(point.x, point.y, 2, 'F');
      
      doc.setFontSize(7);
      doc.text(point.name, point.x, chartY + chartHeight + 7, { align: 'center' });
      
      doc.text(point.value.toString(), point.x, point.y - 5, { align: 'center' });
    });
    
    currentY += chartHeight + 20;
  }
  
  doc.setFontSize(14);
  doc.text('Inventory Items', 15, currentY);
  currentY += 10;
  
  if (filteredItems.length === 0) {
    doc.setFontSize(10);
    doc.text('No data found for the selected period.', 15, currentY);
    currentY += 10;
  } else {
    const tableData = filteredItems.map(item => [
      item.name,
      item.category,
      item.quantity.toString(),
      item.has_expiry && item.expiry_date 
        ? format(new Date(item.expiry_date), 'PPP') 
        : 'No Expiry',
      item.location || 'N/A',
      getItemStatus(item)
    ]);
    
    let tableEnd = 0;
    
    // Using the imported autoTable function directly
    autoTable(doc, {
      startY: currentY,
      head: [['Item Name', 'Category', 'Qty', 'Expiry Date', 'Location', 'Status']],
      body: tableData,
      theme: 'grid',
      headStyles: { 
        fillColor: [5, 150, 105],
        textColor: 255,
        fontStyle: 'bold'
      },
      styles: {
        fontSize: 8,
        cellPadding: 2,
      },
      columnStyles: {
        0: { cellWidth: 35 },
        1: { cellWidth: 30 },
        2: { cellWidth: 10 },
        3: { cellWidth: 30 },
        4: { cellWidth: 30 },
        5: { cellWidth: 20 }
      },
      didDrawPage: function (data: any) {
        tableEnd = data.cursor.y + 15;
      }
    });
    
    // Update currentY after table is drawn
    currentY = tableEnd;
  }
  
  if (currentY > doc.internal.pageSize.getHeight() - 60) {
    doc.addPage();
    currentY = 20;
  }
  
  if (aiSummary) {
    console.log("Adding AI summary to PDF:", aiSummary.substring(0, 100) + "...");
    
    doc.setFontSize(14);
    doc.text('AI-Generated Insights', 15, currentY);
    currentY += 8;
    
    doc.setFontSize(9);
    const textLines = doc.splitTextToSize(aiSummary, 180);
    
    if (currentY + (textLines.length * 4) > doc.internal.pageSize.getHeight() - 20) {
      doc.addPage();
      currentY = 20;
      doc.text('AI-Generated Insights (continued)', 15, currentY);
      currentY += 8;
    }
    
    doc.text(textLines, 15, currentY);
  } else {
    console.log("No AI summary provided for PDF");
  }
  
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text(
      `Page ${i} of ${pageCount} | Generated on ${format(new Date(), 'PPP')} by ExpiTrack`,
      105,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    );
  }
  
  const fileName = `Inventory_Report_${user?.email || 'User'}_${format(new Date(), 'yyyy-MM-dd')}.pdf`;
  doc.save(fileName);
  
  return fileName;
};

export const generateAiSummary = async (stats: ReturnType<typeof calculateStats>, items: InventoryItem[]) => {
  try {
    console.log("Generating AI summary...");
    
    const itemsData = items.slice(0, 10).map(item => ({
      name: item.name,
      category: item.category,
      expiry: item.expiry_date ? format(new Date(item.expiry_date), 'PPP') : 'No expiry'
    }));
    
    const statsForAI = {
      totalItems: stats.totalItems,
      healthyItems: stats.healthyItems,
      healthyPercent: Math.round((stats.healthyItems / stats.totalItems) * 100) || 0,
      expiringSoonItems: stats.expiringSoonItems,
      expiringSoonPercent: Math.round((stats.expiringSoonItems / stats.totalItems) * 100) || 0,
      expiredItems: stats.expiredItems,
      expiredPercent: Math.round((stats.expiredItems / stats.totalItems) * 100) || 0,
      averageShelfLife: stats.averageShelfLife,
      topCategories: stats.categoryData.map(c => `${c.name}: ${c.value} items`).join(', '),
      expiryTrend: stats.trendData.map(t => `${t.name}: ${t.value} items`).join(', '),
      sampleItems: itemsData
    };
    
    console.log("Calling Supabase function with stats:", JSON.stringify(statsForAI).substring(0, 200) + "...");
    
    const { data, error } = await supabase.functions.invoke('generate-inventory-summary', {
      body: { reportData: statsForAI }
    });
    
    if (error) {
      console.error("Error calling generate-inventory-summary:", error);
      throw new Error(error.message);
    }
    
    console.log("Received AI summary:", data?.summary.substring(0, 100) + "...");
    return data.summary;
    
  } catch (error) {
    console.error("Error generating AI summary:", error);
    return generateFallbackSummary(stats);
  }
};
